import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@castlery/shared-redux-store';
import { EVENTS_NAMES_MAP } from '../events-name';
import { gaTrack, getEventRandomId } from '../utils';
import { selectedActiveUser } from '@castlery/modules-user-domain';
import { EcEnv } from '@castlery/config';
import { getProductNeedTracking, TRACKING_MSGS_MAP } from '../helpers';
import type { LineItem } from '@castlery/types';
import { trackFacebookAddToCartEvent } from './fb-capi-events.trigger';
import { trackPinterestAddToCartEvent } from './pinterest-capi-events.trigger';
import { trackAddSwatchEvent } from './swatch.trigger';
import { trackDYAddToCartEvent } from './dy-events.trigger';
import { selectOrderLineItems } from '@castlery/modules-order-domain';
import { trackKlaviyoAddedToCartEvent } from './klaviyo-events.trigger';
import { logger } from '@castlery/observability';

export const trackAddToCartEvent = createAsyncThunk(
  'tracking/trackAddToCartEvent',
  async (
    payload: {
      trackedProduct: ReturnType<typeof getProductNeedTracking>;
      cartLineItems: ReturnType<typeof selectOrderLineItems>;
      qtyIncrements: number;
      itemTotal: string;
      atcType?: 'regular' | '1click';
    },
    { dispatch, getState, fulfillWithValue }
  ) => {
    if (!payload.trackedProduct || !payload.cartLineItems) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }

    try {
      const state = getState() as RootState;
      const customer = selectedActiveUser(state);
      const { trackedProduct } = payload;
      const eventId = getEventRandomId(EVENTS_NAMES_MAP.GA_ADD_TO_CART);

      // 1. trigger ga add to cart event
      const params = {
        event: EVENTS_NAMES_MAP.GA_ADD_TO_CART,
        userStatus: customer ? 'logged-in' : 'logged-out',
        userEmail: customer?.emailHashed || '',
        userEmail2: customer?.email || '',
        eventId: eventId,
        ecommerce: {
          currencyCode: EcEnv.NEXT_PUBLIC_CURRENCY,
          add: {
            products: [trackedProduct],
          },
        },
        atc_type: payload.atcType || 'regular',
      };
      gaTrack(params);
      // 2. trigger facebook add to cart event
      await dispatch(
        trackFacebookAddToCartEvent({
          eventId,
          variant: { name: trackedProduct.name, sku: trackedProduct.id },
          originalPrice: trackedProduct.price,
        })
      );
      // 3. trigger pinterest add to cart event
      await dispatch(
        trackPinterestAddToCartEvent({
          eventId,
          variant: { sku: trackedProduct.id, name: trackedProduct.name },
          originalPrice: trackedProduct.price,
        })
      );
      // 4. trigger dy add to cart event
      await dispatch(
        trackDYAddToCartEvent({
          cartLineItems: payload.cartLineItems,
          variant: { sku: trackedProduct.id, name: trackedProduct.name, quantity: trackedProduct.quantity },
        })
      );
      // 5. trigger klaviyo add to cart event
      await dispatch(
        trackKlaviyoAddedToCartEvent({
          targetVariantSku: trackedProduct.id,
          qtyIncrements: payload.qtyIncrements,
          cartLineItems: payload.cartLineItems,
          itemTotal: payload.itemTotal,
        })
      );
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (error) {
      logger.error('trackAddToCartEvent failed', { error });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackRemoveFromCartEvent = createAsyncThunk(
  'tracking/trackRemoveFromCartEvent',
  async (
    payload: {
      product: ReturnType<typeof getProductNeedTracking>;
    },
    { fulfillWithValue }
  ) => {
    const { product } = payload;
    if (!product) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    try {
      const params = {
        event: EVENTS_NAMES_MAP.GA_REMOVE_FROM_CART,
        ecommerce: {
          currencyCode: EcEnv.NEXT_PUBLIC_CURRENCY,
          remove: {
            products: [product],
          },
        },
      };
      gaTrack(params);
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (error) {
      logger.error('trackRemoveFromCartEvent failed', { error });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackCartActionEvent = createAsyncThunk(
  'tracking/trackCartActionEvent',
  async (
    payload: {
      actionType: 'add' | 'increase' | 'decrease' | 'remove';
      lineItem: LineItem;
      quantityDifference?: number;
    },
    { getState, fulfillWithValue, dispatch }
  ) => {
    if (!payload.actionType || !payload.lineItem) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }

    try {
      const rootState = getState() as RootState;
      const { actionType, lineItem, quantityDifference = 1 } = payload;
      const isSwatch = !!lineItem?.is_swatch;
      const isIncreased = actionType === 'increase' || actionType === 'add';
      const { variant } = lineItem;
      // add swatch
      if (isSwatch) {
        if (isIncreased) {
          return await dispatch(
            trackAddSwatchEvent({
              sku: variant.sku,
              skuName: variant.name,
            })
          );
        }
      }

      const cartLineItems = selectOrderLineItems(rootState);
      // add to cart
      const trackedProduct = getProductNeedTracking(lineItem, quantityDifference);
      if (isIncreased) {
        return await dispatch(
          trackAddToCartEvent({
            trackedProduct: trackedProduct,
            cartLineItems,
            qtyIncrements: quantityDifference,
            itemTotal: cartLineItems?.reduce((acc, item) => acc + Number(item.variant.price) * item.quantity, 0) ?? '',
          })
        );
      }

      // remove from cart
      return await dispatch(
        trackRemoveFromCartEvent({
          product: trackedProduct,
        })
      );
    } catch (error) {
      logger.error('trackCartEvent failed', { error });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);
